import { useState } from "react";
import { accountSchemas, type accountTypes } from "@pineseed/spec";
import { EntityTable, type Column } from "../components/EntityTable";
import { EntityForm } from "../components/EntityForm";
import { Modal } from "../components/Modal";
import { useAdmin } from "../context/AdminContext";
import { useCopyToClipboard } from "@pineseed/gui/hook/useCopyToClipboard";
import { CopyableText } from "@pineseed/gui/component/CopyableText";

type Workspace = accountTypes["Workspace"];

export function WorkspacesPanel() {
  const { account: accountAdmin, adminApp } = useAdmin();
  const { workspaces, organizations } = accountAdmin;
  const { copyToClipboard, isCopied } = useCopyToClipboard();
  
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingWorkspace, setEditingWorkspace] = useState<Workspace | null>(null);
  const [provisioningPublic, setProvisioningPublic] = useState(false);
  const [provisionError, setProvisionError] = useState<string | null>(null);

  // CRUD handlers
  const handleCreate = async (data: Partial<Workspace>) => {
    try {
      await workspaces.createEntity({
        organizationId: data.organizationId!,
        name: data.name!,
        ...(data.key && { key: data.key }),
        ...(data.metadata && { metadata: data.metadata }),
      });
      setShowCreateForm(false);
    } catch (error) {
      console.error('Failed to create workspace:', error);
      throw error;
    }
  };

  const handleEdit = async (data: Partial<Workspace>) => {
    if (!editingWorkspace) return;
    try {
      await workspaces.updateEntity(editingWorkspace.id, data);
      setEditingWorkspace(null);
    } catch (error) {
      console.error('Failed to update workspace:', error);
      throw error;
    }
  };

  const handleDelete = async (workspace: Workspace) => {
    if (!confirm(`Delete workspace "${workspace.name}"?`)) {
      return;
    }
    try {
      await workspaces.deleteEntity(workspace.id);
    } catch (error) {
      console.error('Failed to delete workspace:', error);
    }
  };

  const handleSetAsGlobal = (workspace: Workspace) => {
    workspaces.setAsGlobalWorkspace(workspace.id);
  };

  const [showOrgSelect, setShowOrgSelect] = useState(false);

  const handleProvisionPublicWorkspace = async (organizationId?: string) => {
    if (organizations.entities.length === 0) {
      setProvisionError("No organizations available. Create an organization first.");
      return;
    }
    const orgId = organizationId || (organizations.entities.length === 1 ? organizations.entities[0].id : null);
    if (!orgId) {
      setShowOrgSelect(true);
      return;
    }
    setShowOrgSelect(false);
    setProvisioningPublic(true);
    setProvisionError(null);
    try {
      const response = await fetch(`${adminApp.httpUrl}/system/public-workspace`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ organizationId: orgId }),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({ error: response.statusText }));
        throw new Error(err.error || `Failed: ${response.status}`);
      }
      await workspaces.loadEntities();
    } catch (error) {
      setProvisionError(error instanceof Error ? error.message : "Failed to provision public workspace");
      console.error("Failed to provision public workspace:", error);
    } finally {
      setProvisioningPublic(false);
    }
  };

  // Table columns
  const workspaceColumns: Column<Workspace>[] = [
    {
      key: 'name',
      label: 'Name',
      sortable: true,
      render: (workspace) => (
        <div>
          <div className="font-medium text-gray-900 dark:text-gray-100">{workspace.name}</div>
          {workspace.key && (
            <div className="text-xs text-gray-500 dark:text-gray-400 font-mono">
              key: {workspace.key}
            </div>
          )}
          {workspace.id === adminApp.workspaceId && (
            <span className="text-xs text-purple-600 dark:text-purple-400 font-semibold">
              (Current Global)
            </span>
          )}
        </div>
      ),
    },
    {
      key: 'organizationId',
      label: 'Organization',
      render: (workspace) => {
        if (!workspace.organizationId) {
          return <span className="text-xs text-gray-400">No Organization</span>;
        }
        const org = organizations.entities.find(o => o.id === workspace.organizationId);
        return (
          <div>
            <div className="text-sm text-gray-900 dark:text-gray-100">{org?.name || 'Unknown'}</div>
            <CopyableText
              id={workspace.organizationId}
              onCopy={copyToClipboard}
              isPulsing={isCopied(workspace.organizationId)}
              variant="muted"
            >
              {workspace.organizationId.slice(0, 8)}...
            </CopyableText>
          </div>
        );
      },
    },
    {
      key: 'id',
      label: 'Workspace ID',
      render: (workspace) => (
        <CopyableText
          id={workspace.id}
          onCopy={copyToClipboard}
          isPulsing={isCopied(workspace.id)}
        >
          {workspace.id.slice(0, 8)}...
        </CopyableText>
      ),
    },
    {
      key: 'createdAt',
      label: 'Created',
      sortable: true,
      render: (workspace) => (
        <span className="text-xs text-gray-600 dark:text-gray-400">
          {new Date(workspace.createdAt).toLocaleDateString()}
        </span>
      ),
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (workspace) => (
        <div className="flex gap-2">
          <button
            onClick={(e) => { e.stopPropagation(); handleSetAsGlobal(workspace); }}
            disabled={workspace.id === adminApp.workspaceId}
            className="text-purple-600 hover:text-purple-800 dark:text-purple-400 dark:hover:text-purple-300 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            title="Set as global workspace"
          >
            Set Global
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); setEditingWorkspace(workspace); }}
            className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 text-sm"
          >
            Edit
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); handleDelete(workspace); }}
            className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 text-sm"
          >
            Delete
          </button>
        </div>
      ),
    },
  ];

  const stats = workspaces.getStats();

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
          Workspaces ({stats.total})
        </h2>
        <div className="flex gap-2">
          <div className="flex gap-2 items-center">
            {showOrgSelect ? (
              <select
                onChange={(e) => { if (e.target.value) handleProvisionPublicWorkspace(e.target.value); }}
                className="px-3 py-2 border rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                defaultValue=""
              >
                <option value="" disabled>Select organization...</option>
                {organizations.entities.map(org => (
                  <option key={org.id} value={org.id}>{org.name}</option>
                ))}
              </select>
            ) : (
              <button
                onClick={() => handleProvisionPublicWorkspace()}
                disabled={provisioningPublic}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50"
              >
                {provisioningPublic ? "Provisioning..." : "Provision Public Workspace"}
              </button>
            )}
          </div>
          <button
            onClick={() => setShowCreateForm(true)}
            className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700"
          >
            Create Workspace
          </button>
        </div>
      </div>

      {(workspaces.error || provisionError) && (
        <div className="bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200 p-4 rounded-lg">
          {workspaces.error || provisionError}
        </div>
      )}

      <EntityTable
        columns={workspaceColumns}
        data={workspaces.entities}
        loading={workspaces.loading}
      />

      {/* Create Modal */}
      <Modal
        isOpen={showCreateForm}
        onClose={() => setShowCreateForm(false)}
        title="Create Workspace"
      >
        <EntityForm
          schema={accountSchemas.CreateWorkspaceRequest}
          onSubmit={handleCreate}
          onCancel={() => setShowCreateForm(false)}
        />
      </Modal>

      {/* Edit Modal */}
      <Modal
        isOpen={!!editingWorkspace}
        onClose={() => setEditingWorkspace(null)}
        title="Edit Workspace"
      >
        {editingWorkspace && (
          <EntityForm
            schema={accountSchemas.UpdateWorkspaceRequest}
            initialData={editingWorkspace}
            onSubmit={handleEdit}
            onCancel={() => setEditingWorkspace(null)}
          />
        )}
      </Modal>
    </div>
  );
}

